import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Button, Spinner, Form, Badge, ProgressBar, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

const PHASE_NAMES = {
  1: 'Product Conceptualization',
  2: 'Product Development',
  3: 'Beta Test / Warm Audience',
  4: 'Soft Launch',
  5: 'Pre-Launch',
  6: 'Hard Launch',
  7: 'Recurring Marketing',
};

const PHASE_SHORT = {
  1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V', 6: 'VI', 7: 'VII',
};

const TRACK_COLORS = {
  product_dev: '#1E3A5F',
  marketing: colors.goldDark,
  seo: '#2d8659',
};

const TRACK_LABELS = {
  product_dev: 'Product Dev',
  marketing: 'Marketing',
  seo: 'SEO / Ranking',
};

function LaunchModule() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  // Launch list
  const [launches, setLaunches] = useState([]);

  // Active launch (dashboard)
  const [activeLaunch, setActiveLaunch] = useState(null);
  const [phases, setPhases] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [assets, setAssets] = useState([]);
  const [reviewAsset, setReviewAsset] = useState(null);
  const [regenDirection, setRegenDirection] = useState('');
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(null);
  const [assetActioning, setAssetActioning] = useState(false);
  const [dashboardView, setDashboardView] = useState('actions'); // 'actions', 'phases', or 'assets'
  const [productMissing, setProductMissing] = useState([]);
  const [productEnrichment, setProductEnrichment] = useState([]);
  const [selectedPhase, setSelectedPhase] = useState(null);

  // Action items
  const [actionItems, setActionItems] = useState({ decisions: [], completed_decisions: [], human_tasks: [], pending_approvals: 0, locked_phase: null, locked_until: null });
  const [showCompletedDecisions, setShowCompletedDecisions] = useState(false);

  // Discovery wizard
  const [showDiscovery, setShowDiscovery] = useState(false);
  const [discoveryStep, setDiscoveryStep] = useState(1);
  const [discoveryData, setDiscoveryData] = useState({});
  const [products, setProducts] = useState([]);
  const [proposedTimeline, setProposedTimeline] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const response = await fetch('/api/auth/check-tier/black', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setHasAccess(data.has_access);
        if (data.has_access) {
          await loadLaunches();
        }
      }
    } catch (err) {
      console.error('Failed to check access:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadLaunches = async () => {
    try {
      const res = await fetch('/api/launches', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setLaunches(data.launches || []);
      }
    } catch (err) {
      console.error('Failed to load launches:', err);
    }
  };

  const deleteLaunch = async (launchId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this launch and all its tasks? This cannot be undone.')) return;
    try {
      const res = await fetch(`/api/launches/${launchId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        setLaunches(prev => prev.filter(l => l.id !== launchId));
        if (activeLaunch?.id === launchId) {
          setActiveLaunch(null);
        }
      }
    } catch (err) {
      console.error('Failed to delete launch:', err);
    }
  };

  const loadLaunchDetail = useCallback(async (launchId) => {
    try {
      const [detailRes, actionsRes] = await Promise.all([
        fetch(`/api/launches/${launchId}`, { credentials: 'include' }),
        fetch(`/api/launches/${launchId}/action-items`, { credentials: 'include' }),
      ]);

      if (detailRes.ok) {
        const detail = await detailRes.json();
        setActiveLaunch(detail.launch);
        setPhases(detail.phases);
        setTasks(detail.tasks);
        setPendingApprovals(detail.pending_approvals);
        setSelectedPhase(detail.launch.current_phase);
        setProductMissing(detail.product_missing_fields || []);
        setProductEnrichment(detail.product_enrichment_fields || []);
      }
      if (actionsRes.ok) {
        const actions = await actionsRes.json();
        setActionItems(actions);
      }
    } catch (err) {
      console.error('Failed to load launch detail:', err);
    }
  }, []);

  // ============================================================================
  // DISCOVERY WIZARD
  // ============================================================================

  const startDiscovery = async () => {
    setShowDiscovery(true);
    setDiscoveryStep(1);
    setDiscoveryData({});
    // Load products for selection
    try {
      const res = await fetch('/api/launches/products-for-discovery', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || []);
      }
    } catch (err) {
      console.error('Failed to load products:', err);
    }
  };

  const updateDiscovery = (field, value) => {
    setDiscoveryData(prev => ({ ...prev, [field]: value }));
  };

  const nextDiscoveryStep = async () => {
    if (discoveryStep === 10) {
      // Propose timeline before final step
      try {
        const res = await fetch('/api/launches/propose-timeline', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(discoveryData),
        });
        if (res.ok) {
          const data = await res.json();
          setProposedTimeline(data);
        }
      } catch (err) {
        console.error('Failed to propose timeline:', err);
      }
    }
    setDiscoveryStep(prev => prev + 1);
  };

  const createLaunch = async () => {
    setCreating(true);
    try {
      // Build the launch name from product
      const selectedProduct = products.find(p => p.id === discoveryData.product_id);
      const launchName = selectedProduct
        ? `${selectedProduct.name} Launch`
        : discoveryData.new_product_name
          ? `${discoveryData.new_product_name} Launch`
          : 'New Product Launch';

      const payload = {
        ...discoveryData,
        name: launchName,
        discovery_answers: discoveryData,
        phase_durations: proposedTimeline?.durations || {},
      };

      const res = await fetch('/api/launches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const data = await res.json();
        setShowDiscovery(false);
        await loadLaunches();
        loadLaunchDetail(data.id);
      }
    } catch (err) {
      console.error('Failed to create launch:', err);
    } finally {
      setCreating(false);
    }
  };

  const renderDiscoveryStep = () => {
    const stepStyle = { minHeight: '300px' };

    switch (discoveryStep) {
      case 1:
        return (
          <div style={stepStyle}>
            <h4>What are you launching?</h4>
            <p className="text-muted">Select a product or describe a new one.</p>
            <div className="d-flex flex-column gap-3">
              {products.length > 0 && products.map(p => (
                <Card
                  key={p.id}
                  className="shadow-sm"
                  style={{
                    cursor: 'pointer',
                    border: discoveryData.product_id === p.id ? `2px solid ${colors.goldMid}` : `1px solid ${colors.greyLight}`,
                  }}
                  onClick={() => updateDiscovery('product_id', p.id)}
                >
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{p.name}</strong>
                        {p.position && <Badge bg="secondary" className="ms-2">{p.position}</Badge>}
                        {p.launch_ready === false && <Badge bg="warning" text="dark" className="ms-2">Incomplete</Badge>}
                        {p.usp && <p className="text-muted small mb-0 mt-1">{p.usp.substring(0, 100)}</p>}
                      </div>
                      {p.sale_price && <span className="text-muted">${p.sale_price}</span>}
                    </div>
                    {discoveryData.product_id === p.id && p.missing_fields && p.missing_fields.length > 0 && (
                      <Alert variant="warning" className="mt-2 mb-0 py-2" style={{ fontSize: '0.82rem' }}>
                        <strong>Missing:</strong> {p.missing_fields.join(', ')}.
                        Without this, the AI will fabricate product details. Add them in the <strong>Product Catalog</strong> first.
                      </Alert>
                    )}
                    {discoveryData.product_id === p.id && (!p.missing_fields || p.missing_fields.length === 0) && p.enrichment_fields && p.enrichment_fields.length > 0 && (
                      <Alert variant="info" className="mt-2 mb-0 py-2" style={{ fontSize: '0.82rem' }}>
                        <strong>Tip:</strong> Adding {p.enrichment_fields.join(' and ')} will improve AI output quality.
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              ))}
              <Card
                className="shadow-sm"
                style={{
                  cursor: 'pointer',
                  border: discoveryData.product_source === 'new' ? `2px solid ${colors.goldMid}` : `1px solid ${colors.greyLight}`,
                }}
                onClick={() => {
                  updateDiscovery('product_source', 'new');
                  updateDiscovery('product_id', null);
                }}
              >
                <Card.Body className="text-center">
                  <strong>+ New Product Idea</strong>
                  <p className="text-muted small mb-0">I'm launching something not in my catalog yet</p>
                </Card.Body>
              </Card>
            </div>
          </div>
        );

      case 2:
        if (discoveryData.product_source === 'new') {
          return (
            <div style={stepStyle}>
              <h4>Describe your new product</h4>
              <Form.Group className="mb-3">
                <Form.Label>Product name</Form.Label>
                <Form.Control
                  value={discoveryData.new_product_name || ''}
                  onChange={e => updateDiscovery('new_product_name', e.target.value)}
                  placeholder="e.g., AI Marketing Masterclass"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>One-sentence description</Form.Label>
                <Form.Control
                  as="textarea" rows={2}
                  value={discoveryData.new_product_description || ''}
                  onChange={e => updateDiscovery('new_product_description', e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Who is it for?</Form.Label>
                <Form.Control
                  value={discoveryData.new_product_audience || ''}
                  onChange={e => updateDiscovery('new_product_audience', e.target.value)}
                />
              </Form.Group>
            </div>
          );
        }
        return (
          <div style={stepStyle}>
            <h4>Confirm your product details are current</h4>
            {(() => {
              const p = products.find(pr => pr.id === discoveryData.product_id);
              if (!p) return <Alert variant="warning">Please go back and select a product.</Alert>;
              return (
                <Card className="shadow-sm">
                  <Card.Body>
                    <h5>{p.name}</h5>
                    {p.usp && <p>{p.usp}</p>}
                    <p className="text-muted">
                      {p.product_type} | {p.position} | ${p.sale_price || 'Price TBD'}
                    </p>
                    <Alert variant="info" className="mb-0">
                      If anything needs updating, visit <strong>Products &gt; Catalog</strong> after creating the launch.
                    </Alert>
                  </Card.Body>
                </Card>
              );
            })()}
          </div>
        );

      case 3:
        return (
          <div style={stepStyle}>
            <h4>Where are you in product creation?</h4>
            {[
              { val: 'idea', label: 'Idea', desc: 'Just an idea, not built yet' },
              { val: 'building', label: 'Building', desc: 'Currently in development' },
              { val: 'testing', label: 'Testing', desc: 'Built, currently in QA / beta testing' },
              { val: 'ready', label: 'Ready', desc: 'Product is complete, ready to launch' },
              { val: 'relaunch', label: 'Re-launch', desc: 'Existing product, launching again' },
            ].map(stage => (
              <Card
                key={stage.val}
                className="mb-2 shadow-sm"
                style={{
                  cursor: 'pointer',
                  border: discoveryData.product_stage === stage.val ? `2px solid ${colors.goldMid}` : `1px solid ${colors.greyLight}`,
                }}
                onClick={() => updateDiscovery('product_stage', stage.val)}
              >
                <Card.Body className="py-2">
                  <strong>{stage.label}</strong>
                  <span className="text-muted ms-2">— {stage.desc}</span>
                </Card.Body>
              </Card>
            ))}
          </div>
        );

      case 4:
        return (
          <div style={stepStyle}>
            <h4>Target launch date</h4>
            <p className="text-muted">When do you want to go live? This can be adjusted later.</p>
            <Alert variant="info" className="py-2" style={{ fontSize: '0.9rem' }}>
              <strong>Pro tip:</strong> Tuesday is the best day to launch — your audience is settled into the work week but not yet checked out. Tuesday through Thursday are all solid. Avoid Mondays (inbox overload) and Fridays (weekend brain).
            </Alert>
            <Form.Group className="mb-3">
              <Form.Control
                type="date"
                value={discoveryData.target_launch_date || ''}
                onChange={e => updateDiscovery('target_launch_date', e.target.value)}
              />
              {discoveryData.target_launch_date && (() => {
                const d = new Date(discoveryData.target_launch_date + 'T12:00:00');
                const day = d.getDay();
                const dayName = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][day];
                if (day === 2) return <Form.Text className="text-success">That's a Tuesday — perfect launch day.</Form.Text>;
                if (day >= 3 && day <= 4) return <Form.Text className="text-success">That's a {dayName} — solid choice.</Form.Text>;
                if (day === 1) return <Form.Text className="text-warning">That's a Monday — consider Tuesday instead (inbox overload on Mondays).</Form.Text>;
                if (day === 5) return <Form.Text className="text-warning">That's a Friday — your audience may be checked out. Consider Tuesday-Thursday.</Form.Text>;
                return <Form.Text className="text-warning">That's a {dayName} — weekends are risky for launches. Consider Tuesday-Thursday.</Form.Text>;
              })()}
            </Form.Group>
            <Form.Check
              type="checkbox"
              label="I'm flexible on timing"
              checked={discoveryData.flexible_timing || false}
              onChange={e => updateDiscovery('flexible_timing', e.target.checked)}
            />
          </div>
        );

      case 5:
        return (
          <div style={stepStyle}>
            <h4>External deadlines or events?</h4>
            <p className="text-muted">Any conferences, seasonal windows, or partner commitments that affect timing?</p>
            <div className="d-flex gap-2 mb-3">
              <Button
                variant={discoveryData.has_external_deadlines === false ? 'dark' : 'outline-dark'}
                onClick={() => { updateDiscovery('has_external_deadlines', false); updateDiscovery('external_deadlines', ''); }}
              >
                No, nothing specific
              </Button>
              <Button
                variant={discoveryData.has_external_deadlines === true ? 'dark' : 'outline-dark'}
                onClick={() => updateDiscovery('has_external_deadlines', true)}
              >
                Yes
              </Button>
            </div>
            {discoveryData.has_external_deadlines && (
              <Form.Control
                as="textarea" rows={3}
                value={discoveryData.external_deadlines || ''}
                onChange={e => updateDiscovery('external_deadlines', e.target.value)}
                placeholder="e.g., Industry conference on June 15, Black Friday, partner webinar scheduled..."
              />
            )}
          </div>
        );

      case 6:
        return (
          <div style={stepStyle}>
            <h4>Membership platform?</h4>
            {[
              { val: 'yes_ghl', label: 'Yes — using GHL', desc: 'Already have a GHL membership area' },
              { val: 'yes_other', label: 'Yes — using another platform', desc: 'Kajabi, Teachable, etc.' },
              { val: 'no_want', label: 'No, but I want one', desc: 'AIPE will set one up in GHL for you' },
              { val: 'no', label: 'No membership needed', desc: 'Not applicable for this product' },
            ].map(opt => (
              <Card
                key={opt.val}
                className="mb-2 shadow-sm"
                style={{
                  cursor: 'pointer',
                  border: discoveryData.membership_platform === opt.val ? `2px solid ${colors.goldMid}` : `1px solid ${colors.greyLight}`,
                }}
                onClick={() => updateDiscovery('membership_platform', opt.val)}
              >
                <Card.Body className="py-2">
                  <strong>{opt.label}</strong>
                  <span className="text-muted ms-2">— {opt.desc}</span>
                </Card.Body>
              </Card>
            ))}
          </div>
        );

      case 7:
        return (
          <div style={stepStyle}>
            <h4>Email platform?</h4>
            {['ghl', 'activecampaign', 'convertkit', 'mailchimp', 'other'].map(p => (
              <Card
                key={p}
                className="mb-2 shadow-sm"
                style={{
                  cursor: 'pointer',
                  border: discoveryData.email_platform === p ? `2px solid ${colors.goldMid}` : `1px solid ${colors.greyLight}`,
                }}
                onClick={() => updateDiscovery('email_platform', p)}
              >
                <Card.Body className="py-2">
                  <strong style={{ textTransform: 'capitalize' }}>{p === 'ghl' ? 'GoHighLevel (GHL)' : p}</strong>
                </Card.Body>
              </Card>
            ))}
          </div>
        );

      case 8:
        return (
          <div style={stepStyle}>
            <h4>Payment processor?</h4>
            {['stripe', 'paypal', 'ghl', 'other'].map(p => (
              <Card
                key={p}
                className="mb-2 shadow-sm"
                style={{
                  cursor: 'pointer',
                  border: discoveryData.payment_processor === p ? `2px solid ${colors.goldMid}` : `1px solid ${colors.greyLight}`,
                }}
                onClick={() => updateDiscovery('payment_processor', p)}
              >
                <Card.Body className="py-2">
                  <strong style={{ textTransform: 'capitalize' }}>{p === 'ghl' ? 'GoHighLevel Payments' : p}</strong>
                </Card.Body>
              </Card>
            ))}
          </div>
        );

      case 9:
        return (
          <div style={stepStyle}>
            <h4>Affiliate program?</h4>
            {[
              { val: 'yes_have', label: 'Yes, I already have one', desc: 'External affiliate system in place' },
              { val: 'yes_setup', label: 'Yes, set it up in GHL for me', desc: 'AIPE will configure GHL affiliates' },
              { val: 'no', label: 'No affiliate program', desc: 'Not using affiliates for this launch' },
            ].map(opt => (
              <Card
                key={opt.val}
                className="mb-2 shadow-sm"
                style={{
                  cursor: 'pointer',
                  border: discoveryData.affiliate_program === opt.val ? `2px solid ${colors.goldMid}` : `1px solid ${colors.greyLight}`,
                }}
                onClick={() => updateDiscovery('affiliate_program', opt.val)}
              >
                <Card.Body className="py-2">
                  <strong>{opt.label}</strong>
                  <span className="text-muted ms-2">— {opt.desc}</span>
                </Card.Body>
              </Card>
            ))}
          </div>
        );

      case 10:
        return (
          <div style={stepStyle}>
            <h4>Email list scope for this launch?</h4>
            <p className="text-muted">Who should receive launch emails?</p>
            {[
              { val: 'entire_list', label: 'Email entire list', desc: 'Default for single launches' },
              { val: 'segments', label: 'Email specific segments only', desc: 'Select which GHL tags/segments' },
              { val: 'no_email', label: 'Do not email list', desc: 'Social + organic only (useful when another launch is already emailing)' },
            ].map(opt => (
              <Card
                key={opt.val}
                className="mb-2 shadow-sm"
                style={{
                  cursor: 'pointer',
                  border: discoveryData.email_list_scope === opt.val ? `2px solid ${colors.goldMid}` : `1px solid ${colors.greyLight}`,
                }}
                onClick={() => updateDiscovery('email_list_scope', opt.val)}
              >
                <Card.Body className="py-2">
                  <strong>{opt.label}</strong>
                  <span className="text-muted ms-2">— {opt.desc}</span>
                </Card.Body>
              </Card>
            ))}
          </div>
        );

      case 11:
        // Timeline proposal
        return (
          <div style={stepStyle}>
            <h4>Your Launch Timeline</h4>
            {!proposedTimeline ? (
              <div className="text-center py-4"><Spinner animation="border" /><p>Calculating timeline...</p></div>
            ) : (
              <>
                {proposedTimeline.warnings?.length > 0 && (
                  <Alert variant="warning">
                    {proposedTimeline.warnings.map((w, i) => <div key={i}>{w}</div>)}
                  </Alert>
                )}
                <div className="mb-3">
                  {Object.entries(proposedTimeline.phases).map(([num, phase]) => {
                    const isCompleted = phase.completed;
                    const isFirstActive = parseInt(num) === proposedTimeline.first_active;
                    return (
                      <div
                        key={num}
                        className="d-flex align-items-center mb-2 p-2 rounded"
                        style={{
                          border: isFirstActive ? `2px solid ${colors.goldMid}` : `1px solid ${colors.greyLight}`,
                          opacity: isCompleted ? 0.45 : 1,
                          backgroundColor: isCompleted ? '#f0f0f0' : isFirstActive ? '#fffdf5' : '#fff',
                        }}
                      >
                        <div style={{ width: '40px', fontWeight: 'bold', color: isCompleted ? colors.greyMid : colors.goldDark }}>
                          {isCompleted ? <span style={{ color: '#28a745' }}>&#10003;</span> : PHASE_SHORT[num]}
                        </div>
                        <div className="flex-grow-1">
                          <strong style={{ color: isCompleted ? colors.greyMid : undefined }}>
                            {PHASE_NAMES[num]}
                          </strong>
                          <div className="text-muted small">
                            {isCompleted
                              ? 'Already complete'
                              : <>
                                  {phase.start} — {phase.end || 'Ongoing'}
                                  {phase.weeks > 0 && ` (${phase.weeks} weeks)`}
                                </>
                            }
                          </div>
                          {isFirstActive && (
                            <Badge bg="success" style={{ fontSize: '0.65rem' }}>Starts today</Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Alert variant="info" className="mb-0">
                  You can adjust phase durations after creation. These are starting estimates based on your product stage.
                </Alert>
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const canAdvanceDiscovery = () => {
    switch (discoveryStep) {
      case 1: return discoveryData.product_id || discoveryData.product_source === 'new';
      case 2: return discoveryData.product_id || discoveryData.new_product_name;
      case 3: return discoveryData.product_stage;
      case 4: return true; // Date is optional
      case 5: return true; // Optional
      case 6: return discoveryData.membership_platform;
      case 7: return discoveryData.email_platform;
      case 8: return discoveryData.payment_processor;
      case 9: return discoveryData.affiliate_program;
      case 10: return discoveryData.email_list_scope;
      case 11: return proposedTimeline;
      default: return true;
    }
  };

  // ============================================================================
  // TASK ACTIONS
  // ============================================================================

  const completeTask = async (taskId, answer) => {
    try {
      const url = `/api/launches/${activeLaunch.id}/tasks/${taskId}/complete`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ decision_answer: answer }),
      });
      if (res.ok) {
        loadLaunchDetail(activeLaunch.id);
      }
    } catch (err) {
      console.error('Failed to complete task:', err);
    }
  };

  const answerDecision = async (taskId, answer) => {
    try {
      const res = await fetch(`/api/launches/${activeLaunch.id}/tasks/${taskId}/decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ answer }),
      });
      if (res.ok) {
        loadLaunchDetail(activeLaunch.id);
      }
    } catch (err) {
      console.error('Failed to answer decision:', err);
    }
  };

  const reopenDecision = async (taskId) => {
    try {
      const res = await fetch(`/api/launches/${activeLaunch.id}/tasks/${taskId}/reopen`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        loadLaunchDetail(activeLaunch.id);
      }
    } catch (err) {
      console.error('Failed to reopen decision:', err);
    }
  };

  // ============================================================================
  // ASSET REVIEW
  // ============================================================================

  const fetchAssets = useCallback(async (statusFilter = 'pending') => {
    if (!activeLaunch) return;
    try {
      const res = await fetch(`/api/launches/${activeLaunch.id}/assets?status=${statusFilter}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAssets(data.assets || []);
      }
    } catch (err) {
      console.error('Failed to fetch assets:', err);
    }
  }, [activeLaunch]);

  const openAssetReview = useCallback(() => {
    setDashboardView('assets');
    fetchAssets('pending');
  }, [fetchAssets]);

  const approveAsset = async (assetId) => {
    setAssetActioning(true);
    try {
      const res = await fetch(`/api/launches/${activeLaunch.id}/assets/${assetId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(selectedOptionIndex !== null ? { selected_index: selectedOptionIndex } : {}),
      });
      if (res.ok) {
        setAssets(prev => prev.filter(a => a.id !== assetId));
        setReviewAsset(null);
        setPendingApprovals(prev => Math.max(0, prev - 1));
        loadLaunchDetail(activeLaunch.id);
      }
    } catch (err) {
      console.error('Failed to approve asset:', err);
    }
    setAssetActioning(false);
  };

  const rejectAsset = async (assetId, reason) => {
    setAssetActioning(true);
    try {
      const res = await fetch(`/api/launches/${activeLaunch.id}/assets/${assetId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason }),
      });
      if (res.ok) {
        setAssets(prev => prev.filter(a => a.id !== assetId));
        setReviewAsset(null);
        setPendingApprovals(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Failed to reject asset:', err);
    }
    setAssetActioning(false);
  };

  const regenerateAsset = async (asset) => {
    setAssetActioning(true);
    try {
      // First reject the current one
      await fetch(`/api/launches/${activeLaunch.id}/assets/${asset.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: regenDirection }),
      });
      // Find the task_num from the asset's task_id
      const taskForAsset = tasks.find(t => t.id === asset.task_id);
      if (taskForAsset) {
        const res = await fetch(`/api/launches/${activeLaunch.id}/tasks/${taskForAsset.task_num}/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ direction: regenDirection.trim() || null }),
        });
        if (res.ok) {
          setRegenDirection('');
          setReviewAsset(null);
          fetchAssets('pending');
        }
      }
    } catch (err) {
      console.error('Failed to regenerate:', err);
    }
    setAssetActioning(false);
  };

  const handleAssetGenerated = useCallback(() => {
    if (activeLaunch) {
      setPendingApprovals(prev => prev + 1);
    }
  }, [activeLaunch]);

  const [assetFilter, setAssetFilter] = useState('pending');

  // ============================================================================
  // RENDER
  // ============================================================================

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Checking access...</p>
      </Container>
    );
  }

  if (!hasAccess) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="text-center shadow" style={{ border: '2px solid #1a1a1a' }}>
              <Card.Body className="p-5">
                <h2 style={{ fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif", letterSpacing: '0.1em' }}>
                  Black Tier Feature
                </h2>
                <p className="text-muted mb-4">
                  The 7-Phase Launch System is exclusively available to Black Tier members.
                </p>
                <Alert variant="dark">
                  <strong>What you get with Black Tier:</strong>
                  <ul className="text-start mt-2 mb-0">
                    <li>Complete 7-Phase Launch System with AI-powered asset generation</li>
                    <li>93 tasks across 7 phases with 3 concurrent tracks</li>
                    <li>Decision Blitz — front-load all strategic decisions in day 1-2</li>
                    <li>Automatic phase advancement as tasks complete</li>
                  </ul>
                </Alert>
                <div className="mt-4">
                  <Button variant="dark" size="lg" href="mailto:strategy@aiprofitengineer.com?subject=Black%20Tier%20Inquiry" className="me-2">
                    Inquire About Black Tier
                  </Button>
                  <Button variant="outline-secondary" onClick={() => navigate('/command-center/campaign-hq')}>
                    Back to Campaign HQ
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // ---- DISCOVERY WIZARD ----
  if (showDiscovery) {
    return (
      <Container className="py-4" style={{ maxWidth: '700px' }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 style={{ fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif", letterSpacing: '0.1em' }}>
            New Launch
          </h2>
          <span className="text-muted">{discoveryStep} of 11</span>
        </div>

        <ProgressBar
          now={(discoveryStep / 11) * 100}
          className="mb-4"
          style={{ height: '4px' }}
          variant="warning"
        />

        {renderDiscoveryStep()}

        <div className="d-flex justify-content-between mt-4">
          <Button
            variant="outline-secondary"
            onClick={() => discoveryStep === 1 ? setShowDiscovery(false) : setDiscoveryStep(prev => prev - 1)}
          >
            {discoveryStep === 1 ? 'Cancel' : 'Back'}
          </Button>

          {discoveryStep < 11 ? (
            <Button
              variant="dark"
              onClick={nextDiscoveryStep}
              disabled={!canAdvanceDiscovery()}
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="dark"
              onClick={createLaunch}
              disabled={creating || !proposedTimeline}
            >
              {creating ? <><Spinner size="sm" className="me-2" />Creating...</> : 'Create Launch'}
            </Button>
          )}
        </div>
      </Container>
    );
  }

  // ---- LAUNCH INDEX (multiple launches) ----
  if (!activeLaunch) {
    return (
      <Container className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 style={{ fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif", letterSpacing: '0.1em' }}>
              7-Phase Launch System
            </h1>
            <p className="text-muted mb-0">Orchestrate your product launches with AI-powered generation and tracking.</p>
          </div>
          <div>
            <Button variant="outline-secondary" className="me-2" onClick={() => navigate('/command-center/campaign-hq')}>
              Campaign HQ
            </Button>
            <Button variant="dark" onClick={startDiscovery}>
              + New Launch
            </Button>
          </div>
        </div>

        {launches.length === 0 ? (
          <Card className="text-center shadow-sm py-5" style={{ border: `2px solid ${colors.goldMid}` }}>
            <Card.Body>
              <h4>Ready to Launch?</h4>
              <p className="text-muted mb-4">
                Create your first product launch. The wizard will walk you through 11 quick questions,
                then AIPE generates your 93-task launch plan across 7 phases.
              </p>
              <Button variant="dark" size="lg" onClick={startDiscovery}>
                Start Your First Launch
              </Button>
            </Card.Body>
          </Card>
        ) : (
          <Row>
            {launches.map(launch => (
              <Col md={6} key={launch.id} className="mb-3">
                <Card
                  className="h-100 shadow-sm"
                  style={{ cursor: 'pointer', border: `1px solid ${colors.greyLight}`, transition: 'all 0.2s' }}
                  onClick={() => loadLaunchDetail(launch.id)}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = colors.goldMid; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = colors.greyLight; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight, fontFamily: "'Trajan Pro', serif", letterSpacing: '0.1em' }}>
                    <strong>{launch.name}</strong>
                  </Card.Header>
                  <Card.Body>
                    <p className="text-muted mb-2">{launch.product_name || 'No product selected'}</p>
                    <div className="d-flex justify-content-between align-items-center">
                      <Badge bg={launch.status === 'active' ? 'success' : 'secondary'}>
                        Phase {PHASE_SHORT[launch.current_phase]} — {PHASE_NAMES[launch.current_phase]}
                      </Badge>
                      <div className="d-flex align-items-center gap-2">
                        {launch.target_launch_date && (
                          <small className="text-muted">Target: {launch.target_launch_date}</small>
                        )}
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={(e) => deleteLaunch(launch.id, e)}
                          title="Delete launch"
                        >×</Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Container>
    );
  }

  // ---- LAUNCH DASHBOARD ----
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const totalTasks = tasks.filter(t => t.task_type !== 'deferred').length;
  const progressPct = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  const currentPhaseData = phases.find(p => p.phase_number === activeLaunch.current_phase);

  const phaseTasks = selectedPhase
    ? tasks.filter(t => t.phase_number === selectedPhase)
    : tasks;

  return (
    <Container fluid className="py-3 px-4">
      {/* TOP BAR */}
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h2 style={{ fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif", letterSpacing: '0.1em', marginBottom: '4px' }}>
            {activeLaunch.name}
          </h2>
          <div className="d-flex align-items-center gap-3">
            <span className="text-muted">{activeLaunch.product_name}</span>
            {activeLaunch.target_launch_date && (
              <span className="text-muted">Target: {activeLaunch.target_launch_date}</span>
            )}
            <Badge bg="success">Phase {PHASE_SHORT[activeLaunch.current_phase]} — {progressPct}% complete</Badge>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" size="sm" onClick={() => navigate('/command-center/war-room')}>
            War Room
          </Button>
          {launches.length > 1 && (
            <Button variant="outline-secondary" size="sm" onClick={() => setActiveLaunch(null)}>
              Switch Launch
            </Button>
          )}
          <Button variant="outline-secondary" size="sm" onClick={() => navigate('/command-center/campaign-hq')}>
            Campaign HQ
          </Button>
          <Button variant="outline-danger" size="sm" onClick={(e) => deleteLaunch(activeLaunch.id, e)}>
            Delete Launch
          </Button>
        </div>
      </div>

      {/* PHASE STRIP */}
      <div className="d-flex gap-1 mb-3" style={{ overflowX: 'auto' }}>
        {phases.map(phase => {
          const isActive = phase.phase_number === activeLaunch.current_phase;
          const isComplete = phase.status === 'completed';
          const isSelected = phase.phase_number === selectedPhase;
          const phaseTaskCount = tasks.filter(t => t.phase_number === phase.phase_number && t.task_type !== 'deferred').length;
          const phaseComplete = tasks.filter(t => t.phase_number === phase.phase_number && t.status === 'completed').length;

          return (
            <div
              key={phase.phase_number}
              onClick={() => { setSelectedPhase(phase.phase_number); setDashboardView('phases'); }}
              style={{
                flex: '1 1 0',
                minWidth: '120px',
                padding: '8px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                border: isSelected ? `2px solid ${colors.goldMid}` : `1px solid ${colors.greyLight}`,
                backgroundColor: isComplete ? colors.greyDarkest : isActive ? '#fff' : '#f8f9fa',
                color: isComplete ? colors.goldLight : colors.greyDarkest,
                transition: 'all 0.2s',
              }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <strong style={{ fontSize: '0.85rem' }}>{PHASE_SHORT[phase.phase_number]}</strong>
                {isComplete && <span style={{ color: colors.goldLight }}>&#10003;</span>}
                {isActive && <span style={{ color: colors.goldMid, fontSize: '0.6rem' }}>&#9679;</span>}
              </div>
              <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>{PHASE_NAMES[phase.phase_number]}</div>
              <div style={{ fontSize: '0.7rem', opacity: 0.6 }}>
                {phase.planned_start} — {phase.planned_end || '...'}
              </div>
              {phaseTaskCount > 0 && (
                <ProgressBar
                  now={(phaseComplete / phaseTaskCount) * 100}
                  style={{ height: '3px', marginTop: '4px' }}
                  variant={isComplete ? 'warning' : 'dark'}
                />
              )}
              {(phase.estimated_revenue > 0 || phase.actual_revenue > 0) && (
                <div style={{ fontSize: '0.7rem', marginTop: '2px', color: isComplete ? colors.goldLight : colors.goldDark }}>
                  {phase.actual_revenue > 0 ? `$${phase.actual_revenue.toLocaleString()}` : `~$${phase.estimated_revenue.toLocaleString()}`}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* PRODUCT COMPLETENESS WARNING */}
      {productMissing.length > 0 && (
        <Alert variant="warning" className="mb-3 d-flex align-items-center justify-content-between">
          <div>
            <strong>Product data incomplete — AI will make things up without this.</strong>
            <div className="small mt-1">Missing: {productMissing.join(', ')}.</div>
          </div>
          <Button variant="outline-dark" size="sm" onClick={() => navigate('/command-center/products/catalog')}>
            Edit in Catalog
          </Button>
        </Alert>
      )}
      {productMissing.length === 0 && productEnrichment.length > 0 && (
        <Alert variant="info" className="mb-3 d-flex align-items-center justify-content-between" style={{ fontSize: '0.85rem' }}>
          <div>
            <strong>Tip:</strong> Adding {productEnrichment.join(' and ')} in the Product Catalog will make AI-generated assets sharper.
          </div>
          <Button variant="outline-dark" size="sm" onClick={() => navigate('/command-center/products/catalog')}>
            Enrich Product
          </Button>
        </Alert>
      )}

      {/* VIEW TOGGLE */}
      <div className="d-flex gap-2 mb-3">
        <Button
          variant={dashboardView === 'actions' ? 'dark' : 'outline-dark'}
          size="sm"
          onClick={() => setDashboardView('actions')}
        >
          My Action Items
        </Button>
        <Button
          variant={dashboardView === 'phases' ? 'dark' : 'outline-dark'}
          size="sm"
          onClick={() => setDashboardView('phases')}
        >
          Phase View
        </Button>
        <Button
          variant={dashboardView === 'assets' ? 'dark' : 'outline-dark'}
          size="sm"
          onClick={openAssetReview}
        >
          Asset Review {pendingApprovals > 0 && <Badge bg="danger" className="ms-1">{pendingApprovals}</Badge>}
        </Button>
      </div>

      {/* ACTION ITEMS VIEW */}
      {dashboardView === 'actions' && (() => {
        const lockedPh = actionItems.locked_phase;
        const unlockedDecisions = lockedPh ? actionItems.decisions.filter(t => t.phase_number < lockedPh).length : actionItems.decisions.length;
        const unlockedHuman = lockedPh ? actionItems.human_tasks.filter(t => t.phase_number < lockedPh).length : actionItems.human_tasks.length;
        return (
        <Row>
          <Col lg={8}>
            {/* Decisions Needed */}
            {actionItems.decisions.length > 0 && (
              <div className="mb-4">
                <h5 style={{ color: unlockedDecisions > 0 ? '#dc3545' : colors.greyMid, fontWeight: 600 }}>
                  Decisions Needed <Badge bg={unlockedDecisions > 0 ? 'danger' : 'secondary'}>{unlockedDecisions}</Badge>
                </h5>
                <TaskListWithPhaseSeparators
                  tasks={actionItems.decisions}
                  accent="#dc3545"
                  onComplete={completeTask}
                  onDecision={answerDecision}
                  onReopen={reopenDecision}
                  onGenerate={handleAssetGenerated}
                  lockedPhase={actionItems.locked_phase}
                  lockedUntil={actionItems.locked_until}
                  launchId={activeLaunch?.id}
                />
              </div>
            )}

            {/* Completed Decisions */}
            {actionItems.completed_decisions && actionItems.completed_decisions.length > 0 && (
              <div className="mb-4">
                <h6
                  style={{ color: colors.greyMid, fontWeight: 600, cursor: 'pointer' }}
                  onClick={() => setShowCompletedDecisions(prev => !prev)}
                >
                  Completed Decisions <Badge bg="secondary">{actionItems.completed_decisions.length}</Badge>
                  <span className="ms-1" style={{ fontSize: '0.7rem' }}>{showCompletedDecisions ? '▼' : '▶'}</span>
                </h6>
                {showCompletedDecisions && (
                  <TaskListWithPhaseSeparators
                    tasks={actionItems.completed_decisions}
                    accent={colors.greyMid}
                    onComplete={completeTask}
                    onDecision={answerDecision}
                    onReopen={reopenDecision}
                    onGenerate={handleAssetGenerated}
                    lockedPhase={null}
                    lockedUntil={null}
                    launchId={activeLaunch?.id}
                  />
                )}
              </div>
            )}

            {/* Human Tasks */}
            {actionItems.human_tasks.length > 0 && (
              <div className="mb-4">
                <h5 style={{ color: unlockedHuman > 0 ? colors.goldDark : colors.greyMid, fontWeight: 600 }}>
                  Human Tasks <Badge bg={unlockedHuman > 0 ? 'warning' : 'secondary'} text={unlockedHuman > 0 ? 'dark' : undefined}>{unlockedHuman}</Badge>
                </h5>
                <TaskListWithPhaseSeparators
                  tasks={actionItems.human_tasks}
                  accent={colors.goldDark}
                  onComplete={completeTask}
                  onGenerate={handleAssetGenerated}
                  lockedPhase={actionItems.locked_phase}
                  lockedUntil={actionItems.locked_until}
                  launchId={activeLaunch?.id}
                />
              </div>
            )}

            {unlockedDecisions === 0 && unlockedHuman === 0 && (
              <Alert variant="success">
                <strong>No action items!</strong> All decisions have been made and human tasks are complete.
                {pendingApprovals > 0 && ' Review your pending approvals to keep things moving.'}
              </Alert>
            )}
          </Col>

          <Col lg={4}>
            {/* Pending Approvals Count */}
            <Card
              className="shadow-sm mb-3"
              style={{ border: `2px solid ${colors.greyDarkest}`, cursor: pendingApprovals > 0 ? 'pointer' : 'default' }}
            >
              <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                <strong>Pending Approvals</strong>
              </Card.Header>
              <Card.Body className="text-center py-4">
                <div style={{ fontSize: '3rem', fontWeight: 'bold', color: pendingApprovals > 0 ? colors.goldDark : colors.greyMid }}>
                  {pendingApprovals}
                </div>
                <p className="text-muted mb-0">assets awaiting review</p>
                {pendingApprovals > 0 && (
                  <Button variant="outline-dark" className="mt-3" size="sm" onClick={openAssetReview}>
                    Review Assets
                  </Button>
                )}
              </Card.Body>
            </Card>

            {/* Progress Summary */}
            <Card className="shadow-sm">
              <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                <strong>Progress</strong>
              </Card.Header>
              <Card.Body>
                <div className="mb-2">
                  <strong>{completedTasks}</strong> of <strong>{totalTasks}</strong> tasks complete
                </div>
                <ProgressBar now={progressPct} label={`${progressPct}%`} variant="warning" />
                <hr />
                {phases.map(p => {
                  const pt = tasks.filter(t => t.phase_number === p.phase_number && t.task_type !== 'deferred');
                  const pc = pt.filter(t => t.status === 'completed').length;
                  return (
                    <div key={p.phase_number} className="d-flex justify-content-between mb-1" style={{ fontSize: '0.85rem' }}>
                      <span>{PHASE_SHORT[p.phase_number]}. {p.name}</span>
                      <span className="text-muted">{pc}/{pt.length}</span>
                    </div>
                  );
                })}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      );
      })()}

      {/* PHASE VIEW */}
      {dashboardView === 'phases' && (
        <div>
          <h4 className="mb-3">
            Phase {PHASE_SHORT[selectedPhase]} — {PHASE_NAMES[selectedPhase]}
            {currentPhaseData && selectedPhase === activeLaunch.current_phase && (
              <Badge bg="success" className="ms-2">Current</Badge>
            )}
          </h4>

          {/* Group by track */}
          {['product_dev', 'marketing', 'seo'].map(track => {
            const trackTasks = phaseTasks.filter(t => t.track === track);
            if (trackTasks.length === 0) return null;

            return (
              <div key={track} className="mb-4">
                <h6 style={{ color: TRACK_COLORS[track], fontWeight: 600, borderBottom: `2px solid ${TRACK_COLORS[track]}`, paddingBottom: '4px' }}>
                  {TRACK_LABELS[track]}
                </h6>
                {trackTasks.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    accent={TRACK_COLORS[track]}
                    onComplete={completeTask}
                    onDecision={answerDecision}
                    onReopen={reopenDecision}
                    onGenerate={handleAssetGenerated}
                    showPhase={false}
                    launchId={activeLaunch?.id}
                  />
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* ASSET REVIEW VIEW */}
      {dashboardView === 'assets' && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">Asset Review</h4>
            <div className="d-flex gap-2">
              {['pending', 'approved', 'rejected'].map(f => (
                <Button
                  key={f}
                  variant={assetFilter === f ? 'dark' : 'outline-dark'}
                  size="sm"
                  onClick={() => { setAssetFilter(f); fetchAssets(f); }}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </Button>
              ))}
            </div>
          </div>

          {assets.length === 0 ? (
            <Alert variant="info">
              No {assetFilter} assets. {assetFilter === 'pending' && 'Generate assets from AI tasks in the Action Items or Phase View.'}
            </Alert>
          ) : (
            <>
              {/* Group assets by phase */}
              {[1,2,3,4,5,6,7].map(phaseNum => {
                const phaseAssets = assets.filter(a => a.phase_number === phaseNum);
                if (phaseAssets.length === 0) return null;
                return (
                  <div key={phaseNum} className="mb-4">
                    <h6 style={{ color: colors.goldDark, fontWeight: 600, borderBottom: `2px solid ${colors.goldDark}`, paddingBottom: '4px' }}>
                      Phase {PHASE_SHORT[phaseNum]} — {PHASE_NAMES[phaseNum]}
                    </h6>
                    {phaseAssets.map(asset => (
                      <Card
                        key={asset.id}
                        className="mb-2 shadow-sm"
                        style={{ borderLeft: `4px solid ${asset.status === 'approved' ? '#28a745' : asset.status === 'rejected' ? '#dc3545' : colors.goldMid}`, cursor: 'pointer' }}
                        onClick={() => { setReviewAsset(asset); setSelectedOptionIndex(asset.content?.selected_index ?? asset.content?.recommended_index ?? null); }}
                      >
                        <Card.Body className="py-2 px-3">
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center gap-2">
                                <strong style={{ fontSize: '0.9rem' }}>{asset.name}</strong>
                                <Badge
                                  bg={asset.status === 'approved' ? 'success' : asset.status === 'rejected' ? 'danger' : 'warning'}
                                  text={asset.status === 'approved' || asset.status === 'rejected' ? undefined : 'dark'}
                                  style={{ fontSize: '0.6rem' }}
                                >
                                  {asset.status === 'generated' ? 'Pending Review' : asset.status}
                                </Badge>
                                <Badge bg="light" text="dark" style={{ fontSize: '0.6rem', border: `1px solid ${colors.greyLight}` }}>
                                  {asset.asset_type}
                                </Badge>
                              </div>
                              {asset.content_preview && (
                                <p className="text-muted small mb-0 mt-1" style={{ maxHeight: '2.6em', overflow: 'hidden' }}>
                                  {asset.content_preview}
                                </p>
                              )}
                            </div>
                            <div className="d-flex gap-1 ms-2">
                              {asset.star_rating && (
                                <Badge bg="light" text="dark" style={{ fontSize: '0.7rem', border: `1px solid ${colors.greyLight}` }}>
                                  {asset.star_rating.toFixed(1)} &#9733;
                                </Badge>
                              )}
                              <Button variant="outline-dark" size="sm" style={{ fontSize: '0.7rem' }}>
                                Review
                              </Button>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* ASSET DETAIL MODAL */}
      <Modal show={!!reviewAsset} onHide={() => { setReviewAsset(null); setRegenDirection(''); setSelectedOptionIndex(null); }} size="lg" scrollable>
        {reviewAsset && (
          <>
            <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <Modal.Title style={{ fontSize: '1.1rem' }}>{reviewAsset.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {/* Status bar */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex gap-2">
                  <Badge
                    bg={reviewAsset.status === 'approved' ? 'success' : reviewAsset.status === 'rejected' ? 'danger' : 'warning'}
                    text={reviewAsset.status === 'approved' || reviewAsset.status === 'rejected' ? undefined : 'dark'}
                  >
                    {reviewAsset.status === 'generated' ? 'Pending Review' : reviewAsset.status}
                  </Badge>
                  <Badge bg="light" text="dark" style={{ border: `1px solid ${colors.greyLight}` }}>
                    Phase {PHASE_SHORT[reviewAsset.phase_number]}
                  </Badge>
                  <Badge bg="light" text="dark" style={{ border: `1px solid ${colors.greyLight}` }}>
                    {reviewAsset.asset_type}
                  </Badge>
                  {reviewAsset.star_rating && (
                    <Badge bg="light" text="dark" style={{ border: `1px solid ${colors.greyLight}` }}>
                      {reviewAsset.star_rating.toFixed(1)} &#9733;
                    </Badge>
                  )}
                </div>
                <small className="text-muted">
                  {reviewAsset.created_at && new Date(reviewAsset.created_at).toLocaleDateString()}
                </small>
              </div>

              {reviewAsset.description && (
                <p className="text-muted mb-3">{reviewAsset.description}</p>
              )}

              {reviewAsset.rejection_reason && (
                <Alert variant="danger" className="mb-3">
                  <strong>Rejection reason:</strong> {reviewAsset.rejection_reason}
                </Alert>
              )}

              {/* Content rendering */}
              <AssetContentRenderer
                content={reviewAsset.content}
                assetType={reviewAsset.asset_type}
                selectedIndex={selectedOptionIndex}
                onSelectIndex={setSelectedOptionIndex}
              />
            </Modal.Body>
            <Modal.Footer>
              {(reviewAsset.status === 'generated' || reviewAsset.status === 'pending_review') && (
                <>
                  <div className="d-flex flex-grow-1 gap-2 me-2">
                    <Form.Control
                      size="sm"
                      placeholder="Direction for regeneration (optional)..."
                      value={regenDirection}
                      onChange={e => setRegenDirection(e.target.value)}
                    />
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={assetActioning}
                      onClick={() => regenerateAsset(reviewAsset)}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {assetActioning ? 'Working...' : 'Regenerate'}
                    </Button>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    disabled={assetActioning}
                    onClick={() => rejectAsset(reviewAsset.id, 'Not what I wanted')}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="dark"
                    size="sm"
                    disabled={assetActioning}
                    onClick={() => approveAsset(reviewAsset.id)}
                  >
                    {assetActioning ? 'Working...' : 'Approve'}
                  </Button>
                </>
              )}
              {reviewAsset.status === 'approved' && (
                <span className="text-success"><strong>Approved</strong> {reviewAsset.approved_at && `on ${new Date(reviewAsset.approved_at).toLocaleDateString()}`}</span>
              )}
            </Modal.Footer>
          </>
        )}
      </Modal>
    </Container>
  );
}


// ============================================================================
// TASK CARD COMPONENT
// ============================================================================

function TaskListWithPhaseSeparators({ tasks, accent, onComplete, onDecision, onReopen, onGenerate, lockedPhase, lockedUntil, launchId }) {
  let lastPhase = null;
  return (
    <>
      {tasks.map(task => {
        const showSeparator = lastPhase !== null && task.phase_number !== lastPhase;
        lastPhase = task.phase_number;
        return (
          <React.Fragment key={task.id}>
            {showSeparator && (
              <div className="d-flex align-items-center my-3" style={{ gap: '8px' }}>
                <div style={{ flex: 1, height: '1px', backgroundColor: colors.greyLight }} />
                <span style={{ fontSize: '0.75rem', color: colors.greyMid, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Phase {PHASE_SHORT[task.phase_number]} — {PHASE_NAMES[task.phase_number]}
                </span>
                <div style={{ flex: 1, height: '1px', backgroundColor: colors.greyLight }} />
              </div>
            )}
            {lastPhase === task.phase_number && !showSeparator && tasks.indexOf(task) === 0 && (
              <div className="d-flex align-items-center mb-2" style={{ gap: '8px' }}>
                <div style={{ flex: 1, height: '1px', backgroundColor: colors.greyLight }} />
                <span style={{ fontSize: '0.75rem', color: colors.greyMid, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Phase {PHASE_SHORT[task.phase_number]} — {PHASE_NAMES[task.phase_number]}
                </span>
                <div style={{ flex: 1, height: '1px', backgroundColor: colors.greyLight }} />
              </div>
            )}
            <TaskCard task={task} accent={accent} onComplete={onComplete} onDecision={onDecision} onReopen={onReopen} onGenerate={onGenerate} showPhase={false} locked={lockedPhase && task.phase_number >= lockedPhase} lockedUntil={lockedUntil} launchId={launchId} />
          </React.Fragment>
        );
      })}
    </>
  );
}


function TaskCard({ task, accent, onComplete, onDecision, onReopen, onGenerate, showPhase = true, locked = false, lockedUntil = null, launchId }) {
  const [decisionInput, setDecisionInput] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedAsset, setGeneratedAsset] = useState(null);
  const [genError, setGenError] = useState('');
  const [existingCheck, setExistingCheck] = useState(null); // { source, type, data } or null
  const [showExisting, setShowExisting] = useState(false);

  const isDeferred = task.task_type === 'deferred';
  const isCompleted = task.status === 'completed';

  return (
    <Card
      className="mb-2 shadow-sm"
      style={{
        borderLeft: `4px solid ${isCompleted ? '#28a745' : isDeferred ? colors.greyLight : accent}`,
        opacity: isDeferred || locked ? 0.5 : isCompleted ? 0.7 : 1,
      }}
    >
      <Card.Body className="py-2 px-3">
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1">
            <div className="d-flex align-items-center gap-2">
              {locked && <span style={{ fontSize: '0.85rem' }}>&#128274;</span>}
              {isCompleted && !locked && <span style={{ color: '#28a745' }}>&#10003;</span>}
              <strong style={{ fontSize: '0.9rem' }}>{task.name}</strong>
              {showPhase && (
                <Badge bg="secondary" style={{ fontSize: '0.65rem' }}>
                  Phase {PHASE_SHORT[task.phase_number]}
                </Badge>
              )}
              <Badge
                bg="light" text="dark"
                style={{ fontSize: '0.6rem', border: `1px solid ${colors.greyLight}` }}
              >
                {task.ai_level.replace('_', ' ')}
              </Badge>
            </div>

            {(expanded || task.task_type === 'decision') && (
              <p className="text-muted small mb-1 mt-1">{task.description}</p>
            )}

            {task.decision_answer && (
              <div className="small mt-1">
                <strong>Decision:</strong> {task.decision_answer}
              </div>
            )}

            {/* Existing asset from Playbook — use or generate new? */}
            {showExisting && existingCheck && expanded && !generatedAsset && (
              <div className="mt-2 p-3" style={{ backgroundColor: '#fffdf5', border: `1px solid ${colors.goldMid}`, borderRadius: '4px', fontSize: '0.85rem' }}>
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <strong style={{ color: colors.goldDark }}>Existing from {existingCheck.source}</strong>
                  <Badge bg="warning" text="dark" style={{ fontSize: '0.6rem' }}>Already Created</Badge>
                </div>
                {existingCheck.type === 'campaign_metaphor' && existingCheck.data && (
                  <div>
                    <p className="mb-1"><strong>{existingCheck.data.product_name}:</strong> {existingCheck.data.metaphor}</p>
                    {existingCheck.data.description && <p className="text-muted mb-0" style={{ fontSize: '0.8rem' }}>{existingCheck.data.description}</p>}
                  </div>
                )}
                {existingCheck.type === 'product_meme' && existingCheck.data && (
                  <div>
                    <p className="mb-1"><strong>{existingCheck.data.product_name}:</strong> "{existingCheck.data.concept}"</p>
                    {existingCheck.data.rationale && <p className="text-muted mb-0" style={{ fontSize: '0.8rem' }}>{existingCheck.data.rationale}</p>}
                  </div>
                )}
                {existingCheck.type === 'epimeme' && existingCheck.data && (
                  <div>
                    <p className="mb-1"><strong>EpiMeme:</strong> "{existingCheck.data.concept}"</p>
                    {existingCheck.data.rationale && <p className="text-muted mb-0" style={{ fontSize: '0.8rem' }}>{existingCheck.data.rationale}</p>}
                  </div>
                )}
                <div className="d-flex gap-2 mt-3">
                  <Button
                    variant="dark"
                    size="sm"
                    style={{ fontSize: '0.75rem' }}
                    onClick={async () => {
                      // Use existing — create asset from it
                      setGenerating(true);
                      try {
                        const res = await fetch(`/api/launches/${launchId}/tasks/${task.task_num}/generate`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          credentials: 'include',
                          body: JSON.stringify({ use_existing: true }),
                        });
                        const data = await res.json();
                        if (data.error) {
                          setGenError(`Failed: ${data.error}`);
                        } else {
                          setGeneratedAsset(data.asset);
                          setShowExisting(false);
                          if (onGenerate) onGenerate(task.id, data.asset);
                        }
                      } catch (err) {
                        setGenError('Failed');
                      }
                      setGenerating(false);
                    }}
                    disabled={generating}
                  >
                    {generating ? 'Saving...' : 'Use This'}
                  </Button>
                  <Button
                    variant="outline-dark"
                    size="sm"
                    style={{ fontSize: '0.75rem' }}
                    onClick={async () => {
                      // Generate new — keep existing visible for reference
                      setGenerating(true);
                      try {
                        const res = await fetch(`/api/launches/${launchId}/tasks/${task.task_num}/generate`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          credentials: 'include',
                          body: JSON.stringify({}),
                        });
                        const data = await res.json();
                        if (data.error) {
                          setGenError(`Generation failed: ${data.error}`);
                        } else {
                          setGeneratedAsset(data.asset);
                          // Keep showExisting true so user can compare
                          if (onGenerate) onGenerate(task.id, data.asset);
                        }
                      } catch (err) {
                        setGenError('Generation failed');
                      }
                      setGenerating(false);
                    }}
                    disabled={generating}
                  >
                    {generating ? 'Generating...' : 'Generate New Instead'}
                  </Button>
                </div>
              </div>
            )}

            {genError && (
              <div className="small mt-1 text-danger">{genError}</div>
            )}

            {generatedAsset && expanded && (
              <div className="mt-2 p-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px', fontSize: '0.82rem' }}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <strong style={{ color: colors.goldDark }}>{generatedAsset.name}</strong>
                  <Badge bg="info" style={{ fontSize: '0.6rem' }}>Pending Review</Badge>
                </div>
                {generatedAsset.preview && (
                  <div className="text-muted">{generatedAsset.preview}</div>
                )}
              </div>
            )}
          </div>

          <div className="d-flex gap-1 ms-2">
            {locked && lockedUntil && (
              <Badge bg="secondary" style={{ fontSize: '0.65rem', whiteSpace: 'nowrap' }}>
                Unlocks ~{new Date(lockedUntil).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}
              </Badge>
            )}
            {!locked && !isCompleted && !isDeferred && task.task_type === 'decision' && (
              <>
                {task.decision_options ? (
                  task.decision_options.map(opt => (
                    <Button
                      key={opt}
                      variant="outline-dark"
                      size="sm"
                      style={{ fontSize: '0.75rem' }}
                      onClick={() => onDecision(task.id, opt)}
                    >
                      {opt}
                    </Button>
                  ))
                ) : (
                  <div className="d-flex gap-1">
                    <Form.Control
                      size="sm"
                      style={{ width: '200px', fontSize: '0.8rem' }}
                      placeholder="Your answer..."
                      value={decisionInput}
                      onChange={e => setDecisionInput(e.target.value)}
                    />
                    <Button
                      variant="dark" size="sm"
                      disabled={!decisionInput}
                      onClick={() => { onDecision(task.id, decisionInput); setDecisionInput(''); }}
                    >
                      Save
                    </Button>
                  </div>
                )}
              </>
            )}

            {isCompleted && task.task_type === 'decision' && onReopen && (
              <Button
                variant="outline-dark"
                size="sm"
                style={{ fontSize: '0.75rem' }}
                onClick={(e) => { e.stopPropagation(); onReopen(task.id); }}
              >
                Change
              </Button>
            )}

            {!locked && !isCompleted && !isDeferred && task.task_type === 'human' && task.task_num === 17 && (
              <Button variant="outline-dark" size="sm" style={{ fontSize: '0.75rem' }} disabled>
                Setup Integration</Button>
            )}
            {!locked && !isCompleted && !isDeferred && task.task_type === 'human' && task.task_num !== 17 && (
              <Button variant="outline-dark" size="sm" style={{ fontSize: '0.75rem' }} onClick={() => onComplete(task.id)}>
                Done
              </Button>
            )}

            {!locked && !isCompleted && !isDeferred && task.task_type === 'ai_asset' && launchId && !generatedAsset && (
              <Button
                variant="outline-dark"
                size="sm"
                disabled={generating}
                style={{ fontSize: '0.7rem' }}
                onClick={async () => {
                  setGenError('');
                  // For tasks 3 & 4, check for existing meme hierarchy data first
                  if ([3, 4].includes(task.task_num) && !showExisting) {
                    try {
                      const checkRes = await fetch(`/api/launches/${launchId}/tasks/${task.task_num}/existing-check`, { credentials: 'include' });
                      const checkData = await checkRes.json();
                      if (checkData.existing) {
                        setExistingCheck(checkData.existing);
                        setShowExisting(true);
                        setExpanded(true);
                        return;
                      }
                    } catch (e) { /* fall through to generate */ }
                  }
                  setGenerating(true);
                  setShowExisting(false);
                  try {
                    const res = await fetch(`/api/launches/${launchId}/tasks/${task.task_num}/generate`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({}),
                    });
                    const data = await res.json();
                    if (data.error) {
                      setGenError(`Generation failed: ${data.error}`);
                    } else {
                      setGeneratedAsset(data.asset);
                      setExpanded(true);
                      if (onGenerate) onGenerate(task.id, data.asset);
                    }
                  } catch (err) {
                    setGenError('Generation failed');
                  }
                  setGenerating(false);
                }}
              >
                {generating ? 'Generating...' : 'Generate'}
              </Button>
            )}
            {!locked && !isDeferred && task.task_type === 'ai_asset' && generatedAsset && (
              <Badge bg="success" style={{ fontSize: '0.65rem' }}>Generated — Review in Asset Review</Badge>
            )}

            {!isDeferred && !expanded && task.task_type !== 'decision' && (
              <Button variant="link" size="sm" className="text-muted p-0" onClick={() => setExpanded(!expanded)}>
                {expanded ? '−' : '+'}
              </Button>
            )}

            {isDeferred && <Badge bg="secondary" style={{ fontSize: '0.65rem' }}>Deferred</Badge>}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

// ============================================================================
// ASSET CONTENT RENDERER — renders different asset content types
// ============================================================================

function AssetContentRenderer({ content, assetType, selectedIndex, onSelectIndex }) {
  if (!content) {
    return <p className="text-muted">No content available.</p>;
  }

  // Handle string content (shouldn't normally happen but be safe)
  if (typeof content === 'string') {
    return <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>{content}</div>;
  }

  // --- Strategy assets with multiple options (signature concept, campaign metaphor, etc.) ---
  if (content.concepts) {
    const hasSelection = onSelectIndex !== undefined;
    const activeIndex = selectedIndex !== null && selectedIndex !== undefined ? selectedIndex : content.selected_index;
    return (
      <div>
        {content.recommended_index !== undefined && (
          <p className="small text-muted mb-2">
            AI Recommended: Option {content.recommended_index + 1}
            {hasSelection && <span> — click an option to select it</span>}
          </p>
        )}
        {content.concepts.map((c, i) => {
          const isSelected = activeIndex === i;
          const isRecommended = content.recommended_index === i;
          return (
            <Card
              key={i}
              className="mb-3"
              style={{
                border: isSelected ? `2px solid ${colors.goldMid}` : `1px solid ${colors.greyLight}`,
                cursor: hasSelection ? 'pointer' : 'default',
                backgroundColor: isSelected ? '#fffdf5' : undefined,
              }}
              onClick={() => hasSelection && onSelectIndex(i)}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <strong>Option {i + 1}</strong>
                  <div>
                    {isSelected && <Badge bg="success" className="me-1">Selected</Badge>}
                    {isRecommended && <Badge bg="warning" text="dark">Recommended</Badge>}
                  </div>
                </div>
                {c.concept && <p className="mb-2" style={{ fontSize: '1.05rem', fontWeight: 500 }}>{c.concept}</p>}
                {c.tagline && <p className="mb-1"><strong>Tagline:</strong> {c.tagline}</p>}
                {c.hook_example && <p className="mb-1"><strong>Hook:</strong> <em>{c.hook_example}</em></p>}
                {c.email_subject_example && <p className="mb-1"><strong>Email subject:</strong> {c.email_subject_example}</p>}
                {c.rationale && <p className="text-muted small mb-0">{c.rationale}</p>}
                {/* Campaign metaphor fields */}
                {c.metaphor && <p className="mb-2" style={{ fontSize: '1.05rem', fontWeight: 500 }}>{c.metaphor}</p>}
                {c.visual_image && <p className="mb-1"><strong>Visual:</strong> {c.visual_image}</p>}
                {c.one_liner && <p className="mb-1"><strong>One-liner:</strong> {c.one_liner}</p>}
              </Card.Body>
            </Card>
          );
        })}
      </div>
    );
  }

  // --- Metaphors array ---
  if (content.metaphors) {
    const hasSelection = onSelectIndex !== undefined;
    const activeIndex = selectedIndex !== null && selectedIndex !== undefined ? selectedIndex : content.selected_index;
    return (
      <div>
        {content.recommended_index !== undefined && (
          <p className="small text-muted mb-2">
            AI Recommended: Option {content.recommended_index + 1}
            {hasSelection && <span> — click an option to select it</span>}
          </p>
        )}
        {content.metaphors.map((m, i) => {
          const isSelected = activeIndex === i;
          const isRecommended = content.recommended_index === i;
          return (
            <Card
              key={i}
              className="mb-3"
              style={{
                border: isSelected ? `2px solid ${colors.goldMid}` : `1px solid ${colors.greyLight}`,
                cursor: hasSelection ? 'pointer' : 'default',
                backgroundColor: isSelected ? '#fffdf5' : undefined,
              }}
              onClick={() => hasSelection && onSelectIndex(i)}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <strong>Option {i + 1}</strong>
                  <div>
                    {isSelected && <Badge bg="success" className="me-1">Selected</Badge>}
                    {isRecommended && <Badge bg="warning" text="dark">Recommended</Badge>}
                  </div>
                </div>
                {Object.entries(m).map(([key, val]) => (
                  <p key={key} className="mb-1"><strong>{key.replace(/_/g, ' ')}:</strong> {typeof val === 'string' ? val : JSON.stringify(val)}</p>
                ))}
              </Card.Body>
            </Card>
          );
        })}
      </div>
    );
  }

  // --- Email sequences ---
  if (content.emails || content.sequence) {
    const emails = content.emails || content.sequence || [];
    return (
      <div>
        {content.sequence_name && <h6 className="mb-3">{content.sequence_name}</h6>}
        {content.overall_strategy && <p className="text-muted mb-3">{content.overall_strategy}</p>}
        {emails.map((email, i) => (
          <Card key={i} className="mb-2" style={{ borderLeft: `3px solid ${colors.goldMid}` }}>
            <Card.Body className="py-2 px-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <strong style={{ fontSize: '0.9rem' }}>
                  {email.day !== undefined ? `Day ${email.day}: ` : `Email ${i + 1}: `}
                  {email.subject || email.subject_line || ''}
                </strong>
                {email.purpose && <Badge bg="light" text="dark" style={{ fontSize: '0.65rem', border: `1px solid ${colors.greyLight}` }}>{email.purpose}</Badge>}
              </div>
              {email.preview_text && <p className="text-muted small mb-1"><em>Preview: {email.preview_text}</em></p>}
              {(email.body || email.content) && (
                <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', maxHeight: '200px', overflow: 'auto', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {email.body || email.content}
                </div>
              )}
              {email.cta && <p className="mt-1 mb-0 small"><strong>CTA:</strong> {email.cta}</p>}
            </Card.Body>
          </Card>
        ))}
      </div>
    );
  }

  // --- Sales page / landing page ---
  if (content.headline || content.sections || content.hero) {
    return (
      <div>
        {content.headline && <h5 className="mb-2">{content.headline}</h5>}
        {content.subheadline && <p className="text-muted mb-3">{content.subheadline}</p>}
        {content.hero && (
          <Card className="mb-3 bg-light">
            <Card.Body>
              <strong>Hero Section</strong>
              {typeof content.hero === 'string' ? <p>{content.hero}</p> : Object.entries(content.hero).map(([k, v]) => (
                <p key={k} className="mb-1"><strong>{k.replace(/_/g, ' ')}:</strong> {typeof v === 'string' ? v : JSON.stringify(v)}</p>
              ))}
            </Card.Body>
          </Card>
        )}
        {content.sections && content.sections.map((s, i) => (
          <Card key={i} className="mb-2">
            <Card.Body className="py-2 px-3">
              <strong>{s.title || s.heading || `Section ${i + 1}`}</strong>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>{s.body || s.content || s.copy || JSON.stringify(s, null, 2)}</div>
            </Card.Body>
          </Card>
        ))}
        {content.cta && <p><strong>CTA:</strong> {typeof content.cta === 'string' ? content.cta : JSON.stringify(content.cta)}</p>}
      </div>
    );
  }

  // --- Social posts ---
  if (content.posts) {
    return (
      <div>
        {content.strategy && <p className="text-muted mb-3">{content.strategy}</p>}
        {content.posts.map((post, i) => (
          <Card key={i} className="mb-2" style={{ borderLeft: `3px solid ${colors.goldMid}` }}>
            <Card.Body className="py-2 px-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <strong style={{ fontSize: '0.85rem' }}>{post.platform || `Post ${i + 1}`}</strong>
                {post.type && <Badge bg="light" text="dark" style={{ fontSize: '0.6rem', border: `1px solid ${colors.greyLight}` }}>{post.type}</Badge>}
              </div>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>{post.content || post.body || post.copy}</div>
              {post.hashtags && <p className="small text-muted mt-1 mb-0">{post.hashtags}</p>}
            </Card.Body>
          </Card>
        ))}
      </div>
    );
  }

  // --- List-based content (keywords, FAQ, etc.) ---
  if (content.items || content.keywords || content.questions) {
    const items = content.items || content.keywords || content.questions || [];
    return (
      <div>
        {content.strategy && <p className="text-muted mb-3">{content.strategy}</p>}
        {content.overview && <p className="text-muted mb-3">{content.overview}</p>}
        {items.map((item, i) => (
          <div key={i} className="mb-2 p-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px', fontSize: '0.85rem' }}>
            {typeof item === 'string' ? item : (
              <>
                {item.keyword && <strong>{item.keyword}</strong>}
                {item.question && <strong>{item.question}</strong>}
                {item.term && <strong>{item.term}</strong>}
                {item.title && <strong>{item.title}</strong>}
                {(item.answer || item.description || item.rationale || item.detail) && (
                  <p className="mb-0 mt-1 text-muted">{item.answer || item.description || item.rationale || item.detail}</p>
                )}
                {item.search_volume && <span className="small text-muted ms-2">Vol: {item.search_volume}</span>}
              </>
            )}
          </div>
        ))}
      </div>
    );
  }

  // --- Generic fallback: render all top-level keys ---
  return (
    <div>
      {Object.entries(content).map(([key, val]) => (
        <div key={key} className="mb-3">
          <strong style={{ fontSize: '0.9rem', textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</strong>
          {typeof val === 'string' ? (
            <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', marginTop: '4px' }}>{val}</div>
          ) : Array.isArray(val) ? (
            <div style={{ marginTop: '4px' }}>
              {val.map((item, i) => (
                <div key={i} className="mb-1 p-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px', fontSize: '0.85rem' }}>
                  {typeof item === 'string' ? item : <pre style={{ fontSize: '0.8rem', marginBottom: 0 }}>{JSON.stringify(item, null, 2)}</pre>}
                </div>
              ))}
            </div>
          ) : typeof val === 'object' && val !== null ? (
            <pre style={{ fontSize: '0.8rem', backgroundColor: '#f8f9fa', padding: '8px', borderRadius: '4px', marginTop: '4px' }}>
              {JSON.stringify(val, null, 2)}
            </pre>
          ) : (
            <div style={{ fontSize: '0.85rem', marginTop: '4px' }}>{String(val)}</div>
          )}
        </div>
      ))}
    </div>
  );
}

export default LaunchModule;
